import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from "prop-types";
import css from './App.scss';
import { IntakePermissions } from 'utils/types';
import { hot } from 'react-hot-loader/root';
import { urlParams } from 'utils/request';
import { isEmpty } from 'lodash';
import { missingRequiredAnswers, submitIntakeForm, scrollToTop, invalidAnswersByType } from 'utils/intake';
import { initialState, reducer } from './reducer';
import useStarfishAPI from 'hooks/useStarfishAPI';
import ErrorMessage from 'common/ErrorMessage';
import SectionOuterContainer from './SectionOuterContainer';
import IntakeElementsContainer from './IntakeElementsContainer';
import ConfirmDialog from 'common/ConfirmDialog';
import Toast from 'common/Toast';
import IntakeFooter from './IntakeFooter';
import NoIntakeSurvey from './NoIntakeSurvey';
import WarningMessage from './WarningMessage';

const App = ({multipleStudentSurveysEnabled, studentId, userPermissions, surveyId}) => {

  const [state, dispatch] = useReducer(reducer, initialState);
  const [hopeResponse, callHopeAPI] = useStarfishAPI();
  const [intakeResponse, callIntakeAPI] = useStarfishAPI();
  const [submitResponse, callSubmitAPI, { resetResponse: resetSubmitResponse }] = useStarfishAPI();
  const [processResponse, callProcessAPI] = useStarfishAPI();
  const [validationError, setValidationError] = useState(null);

  const formHasError = state.intakeFormElements !== null 
    ? invalidAnswersByType(state.intakeFormElements) 
    : true;
  const incompleteForm = state.intakeFormElements !== null 
    ? missingRequiredAnswers(state.intakeFormElements) 
    : true;

  const canSubmit = !submitResponse.pending && !processResponse.pending && !formHasError && !incompleteForm;

  useEffect(() => {
    state.intakeFormElements && formHasError  
      ? setValidationError('There was an error on one of your responses.') 
      : setValidationError(null);
  }, [formHasError]);

  const intakeFormUnanswered = intakeResponse.data.intakeForm
    && intakeResponse.data.intakeForm.elements.length > 0 
    && !intakeResponse.data.intakeForm.hasAnswers;

  const getIntakeFooter = () => {
    if ( !state.isEditable ) return null;

    // If we are in the hope form and the intake form exists and is unanswered
    // display language indicating that there will be another form
    const hasAnotherForm = state.isHopeForm && intakeFormUnanswered;
    const saveText = submitResponse.pending || processResponse.pending ? "Saving" : `Save & ${hasAnotherForm ? "Next" : "Submit"}`;
    const showCancelButton = !state.isHopeForm && intakeResponse.data.intakeForm.hasAnswers;
    return (
      <IntakeFooter
        saveText={saveText}
        showCancelButton={showCancelButton}
        canSubmit={canSubmit}
        onSubmitButtonClick={() => dispatch({ type: "TOGGLE_SUBMIT_DIALOG" })}
        onCancelButtonClick={() => dispatch({ type: "TOGGLE_CANCEL_DIALOG" })}
      />
    );
  };
  
  const getToast = () => {
    return state.showToast
      ? <Toast message="Your response was saved." onTimeout={() => dispatch({ type: "TOGGLE_TOAST" })} />
      : null;
  };

  const getError = () => {
    const errorMessage = hopeResponse.error || intakeResponse.error || submitResponse.error || processResponse.error || validationError;
    return errorMessage ? <ErrorMessage message={errorMessage} /> : null;
  };
  
  const callHopeCalculations = async () => {
    await callProcessAPI(`../intake/calculateHopeScores.html?studentId=${studentId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    scrollToTop();
  };

  useEffect(() => {
    if (multipleStudentSurveysEnabled) {
      callIntakeAPI('../student-surveys/getStudentSurvey.html?' + urlParams({studentId, surveyId}, false));
      return;
    }
    if(userPermissions.isHopeCenterIntakeEnabled && userPermissions.viewingAsStudent){
      callHopeAPI('../intake/getHopeIntakeForm.html?' + urlParams({studentId}, false));
    }
    callIntakeAPI('../student/getIntakeForm.html?' + urlParams({studentId}, false));
  }, [userPermissions]);

  useEffect(() => {
    // Get permissions first
    const callHopeCenterAPI = userPermissions.isHopeCenterIntakeEnabled && userPermissions.viewingAsStudent;
    const canEditIntakeForm = userPermissions.canEditIntakeForm;
    
    // Do not render anything until both API resolve
    if ( callHopeCenterAPI && isEmpty(hopeResponse.data.intakeForm) || isEmpty(intakeResponse.data.intakeForm) ) return;

    // If user has permission to edit, hope center is enabled but never answered, render hope questions
    if ( canEditIntakeForm && callHopeCenterAPI && !hopeResponse.data.intakeForm.hasAnswers ) {
      dispatch({ type: "LOAD_HOPE_QUESTIONS", elements: hopeResponse.data.intakeForm.elements });
    }
    // If user has permission to edit and never answered intake questions, render intake questions
    else if ( canEditIntakeForm && intakeFormUnanswered ) {
      dispatch({ 
        type: "LOAD_INTAKE_QUESTIONS",
        title: intakeResponse.data.intakeForm.title,
        viewerIsAuthor: intakeResponse.data.intakeForm.viewerIsAuthor,
        author: intakeResponse.data.intakeForm.author,
        elements: intakeResponse.data.intakeForm.elements,
        lastUpdated: intakeResponse.data.intakeForm.updatedDate,
        abbreviatedTZ: intakeResponse.data.intakeForm.abbreviatedTZ
      });
    }
    // In this case we just render the intake questions in read only mode
    else {
      dispatch({ 
        type: "VIEW_QUESTIONS",
        title: intakeResponse.data.intakeForm.title,
        viewerIsAuthor: intakeResponse.data.intakeForm.viewerIsAuthor,
        author: intakeResponse.data.intakeForm.author,
        elements: intakeResponse.data.intakeForm.elements,
        lastUpdated: intakeResponse.data.intakeForm.updatedDate,
        abbreviatedTZ: intakeResponse.data.intakeForm.abbreviatedTZ
      });
    }
  }, [hopeResponse, intakeResponse, userPermissions]);

  useEffect(() => {
    if ( state.isHopeForm && submitResponse.success ) callHopeCalculations();
  }, [submitResponse.success, state.isHopeForm, intakeResponse]);

  useEffect(() => {
    if ( !submitResponse.success || (state.isHopeForm && !processResponse.success )) return;

    if (multipleStudentSurveysEnabled) {
      callIntakeAPI('../student-surveys/getStudentSurvey.html?' + urlParams({studentId, surveyId}, false));
    } else if ( state.isHopeForm ) {
      callHopeAPI('../intake/getHopeIntakeForm.html?' + urlParams({studentId}, false));
    } else {
      callIntakeAPI('../student/getIntakeForm.html?' + urlParams({studentId}, false));
    }

    dispatch({ type: "TOGGLE_TOAST" });
    resetSubmitResponse();
  }, [submitResponse.success, processResponse.success, state.isHopeForm, intakeResponse]);

  const getContent = () => {
    if ( multipleStudentSurveysEnabled && !surveyId ) return <ErrorMessage message={"This feature is disabled."} />;
    if ( !state.intakeFormElements ) return null;
    if ( hopeResponse.pending || intakeResponse.pending ) return <div>Loading...</div>;
    if ( !state.isHopeForm && state.intakeFormElements.length === 0 ) 
      return <NoIntakeSurvey />

    return (
      <>
        <SectionOuterContainer
          surveyTitle={state.surveyTitle}
          authorName={state.viewerIsAuthor ? "you" : `${state.author?.firstName} ${state.author?.lastName}`}
          viewingAsStudent={userPermissions.viewingAsStudent}
          studentId={studentId}
          isEditable={state.isEditable}
          canEditIntakeForm={userPermissions.canEditIntakeForm}
          lastUpdatedDate={state.intakeFormLastUpdated}
          abbreviatedTZ={state.abbreviatedTZ}
          onEditClick={() => dispatch({ type: "SET_IS_EDITABLE" })}
          isHopeForm={state.isHopeForm}
          multipleStudentSurveysEnabled={multipleStudentSurveysEnabled}
        >
          <IntakeElementsContainer 
            intakeElements={state.intakeFormElements} 
            setValue={(questionId, value) => dispatch({ type: "SET_VALUE", questionId, value })}
            isReadOnly={!state.isEditable}
          />
          {state.isHopeForm && <WarningMessage message="Thank you for sharing your experiences with us. You will not be able to view or edit this section of the survey after clicking Save." />}
          {getIntakeFooter()}
        </SectionOuterContainer>
        <ConfirmDialog
          appElementId="main-center"
          isOpen={state.isSubmitDialogOpen}
          onConfirm={() => {
            dispatch({ type: "TOGGLE_SUBMIT_DIALOG" });
            submitIntakeForm(callSubmitAPI, studentId, surveyId, state.intakeFormElements, multipleStudentSurveysEnabled);
          }}
          onClose={() => dispatch({ type: "TOGGLE_SUBMIT_DIALOG" })}
          confirmText="Yes, submit"
          cancelText="Keep editing"
          headerText="Are you sure you want to submit?"
          descriptionText={state.isHopeForm ? "You won't be able to edit your responses later." : "You can return and edit your responses later."}
        />
        <ConfirmDialog
          appElementId="main-center"
          isOpen={state.isCancelDialogOpen}
          onConfirm={() => {
            dispatch({ 
              type: "CANCEL_FORM", 
              elements: intakeResponse.data.intakeForm.elements 
            });
            scrollToTop();
          }}
          onClose={() => dispatch({ type: "TOGGLE_CANCEL_DIALOG" })}
          confirmText="Yes, cancel"
          cancelText="No, go back"
          headerText="Are you sure you want to cancel?"
          descriptionText="Your changes in the survey will not be saved."
        />
        {getToast()}
      </>
    )
  }

  return (
    <div className={css['app-container']}>
      {getError()}
      {getContent()}
    </div>
  );
};

App.propTypes = {
  multipleStudentSurveysEnabled: PropTypes.bool,
  userPermissions: IntakePermissions.isRequired,
  studentId: PropTypes.number.isRequired,
  surveyId: PropTypes.number
};

export default hot(App);
